import { useState, type RefObject } from 'react'
import { circleByType } from '../shared'
import type { IndicatorType } from 'shared/types'
import type { Radar } from 'shared'

export function BaseHover(props: {
  forwardRef: RefObject<SVGSVGElement | null>
  indicators: Radar['indicators'] | undefined
}) {
  const texts = {
    competences: calcPositionForEachIndicator(props.indicators, 'competences', 18, 80),
    vectors: calcPositionForEachIndicator(props.indicators, 'vectors', 6, 60),
    dimensions: calcPositionForEachIndicator(props.indicators, 'dimensions', 3, 30),
  }
  const { typeHovered, handleMouseEnter, handleMouseLeave } = useHoverIndicators(props.forwardRef, 'var(--radar-hover)')

  return (
    <>
      {/* One hit path per ring TYPE, not per segment. Every segment of a ring carried identical
          attributes and the handlers only ever read `data-type`, so 27 elements collapse to 3 with the
          same hit area (the segments do not overlap, so fill-rule never comes into it) and the same
          paint — `transparent` still hit-tests. */}
      {circleByType.map((ring) => (
        <path
          key={ring.type}
          data-type={ring.type}
          className="base-hover"
          fill={ring.type === 'vectors' && typeHovered === 'dimensions' ? 'var(--radar-hover)' : 'transparent'}
          stroke="transparent"
          strokeMiterlimit="10"
          d={ring.d}
          onPointerEnter={handleMouseEnter}
          onPointerLeave={handleMouseLeave}
        />
      ))}

      {texts[typeHovered]?.map(({ title, index, x, y }) => (
        // Drop the redundant <g> wrapper — text already carries its own translate.
        // (cx/cy on <g> are not valid SVG attributes.)
        <text
          key={`${typeHovered}-${index}`}
          fontSize="9"
          fill="rgb(var(--text))"
          transform={`translate(${x} ${y})`}
          textAnchor="middle"
          // To align properly with spaces the value 'Empoderamiento'
          style={{ whiteSpace: 'pre' }}
          className="animate"
        >
          {title}
        </text>
      ))}
    </>
  )
}

export const calcPositionForEachIndicator = (
  indicators: Record<IndicatorType, { index: number; title: string }[]> | undefined,
  type: IndicatorType,
  size: number,
  extraAngle: number,
) => {
  const offset = {
    competences: { x: 180 + 20, y: 180, offsetX: 162, offsetY: 162 },
    vectors: { x: 140, y: 140, offsetX: 162, offsetY: 162 },
    dimensions: { x: 90, y: 90, offsetX: 162, offsetY: 162 },
  }

  return indicators?.[type].map((indicator) => {
    const deg = (360 / size) * indicator.index
    const angle = ((deg - extraAngle) / 180) * Math.PI
    const distance = indicator.title.split(' ').length > 1 ? indicator.title.length : 0

    return {
      // To align properly with spaces the value 'Empoderamiento'
      title: indicator.index === 9 ? `${indicator.title}      ` : indicator.title,
      index: indicator.index,
      x: Math.round((offset[type].x + distance) * Math.cos(angle)) + offset[type].offsetX,
      y: Math.round(offset[type].y * Math.sin(angle)) + offset[type].offsetY,
    }
  })
}

const useHoverIndicators = (forwardRef: RefObject<SVGSVGElement | null>, colorOnHover: string) => {
  const [hovered, setHovered] = useState<'competences' | 'vectors' | 'dimensions'>('competences')

  const handleMouseEnter = (event: React.PointerEvent<SVGPathElement>) => {
    const elem = event.currentTarget
    const type = elem.dataset.type as 'competences' | 'vectors' | 'dimensions'

    setHovered(type)
    const polylineVector = forwardRef.current?.querySelector('.polyline-vector') as unknown as SVGPolylineElement | null
    if (polylineVector) {
      polylineVector.style.opacity = type === 'dimensions' ? '0' : '0.4'
    }

    // Avoid scores showing without opacity outside hovered zone
    forwardRef.current
      ?.querySelector('.hide-overflow-scores-at-hover')
      ?.setAttribute('stroke', type !== 'competences' ? colorOnHover : 'transparent')
  }

  const handleMouseLeave = () => {
    setHovered('competences')
    const polylineVector = forwardRef.current?.querySelector('.polyline-vector') as unknown as SVGPolylineElement | null
    if (polylineVector) {
      polylineVector.style.opacity = '0.4'
    }

    forwardRef.current?.querySelector('.hide-overflow-scores-at-hover')?.setAttribute('stroke', 'transparent')
  }

  return { typeHovered: hovered, handleMouseEnter, handleMouseLeave }
}
